import {
  DopplerUserApiClient,
  ContactInformation,
  Features,
  IntegrationsStatus,
  CollaboratorInvite,
  CollaboratorSection,
  BaseCollectionPage,
  SendCollaboratorInviteData,
  UpdateCollaboratorData,
  AccountNotificationSettings,
} from './doppler-user-api-client';
import { EmptyResultWithoutExpectedErrors, ResultWithoutExpectedErrors } from '../doppler-types';
import { timeout } from '../utils';

export const fakeContactInformation = {
  email: 'test@makingsense.com',
  firstname: 'Test',
  lastname: 'Test',
  address: 'Alem 1234',
  city: 'Tandil',
  province: 'AR-B',
  country: 'ar',
  zipCode: '7000',
  phone: '+54 249 422-2222',
  company: 'Making Sense',
  industry: 'dplr1',
  idSecurityQuestion: '1',
  answerSecurityQuestion: 'answer',
};

const featuresResult = {
  contactPolicies: true,
  bigQuery: true,
};

const integrationsStatusResult: IntegrationsStatus = {
  apiKeyStatus: 'connected',
  dkimStatus: 'alert',
  customDomainStatus: 'disconnected',
  tiendanubeStatus: 'connected',
  TokkoBrokerStatus: 'disconnected',
  datahubStatus: 'disconnected',
  prestashopStatus: 'disconnected',
  shopifyStatus: 'disconnected',
  magentoStatus: 'alert',
  zohoStatus: 'connected',
  wooCommerceStatus: 'disconnected',
  easycommerceStatus: 'connected',
  bmwRspCrmStatus: 'alert',
  vtexStatus: 'alert',
  googleAnaliyticStatus: 'connected',
  bigQueryStatus: 'disconnected',
  MercadoLibreStatus: 'connected',
  MiTiendaStatus: 'connected',
  JumpsellerStatus: 'disconnected',
};

const collaborationInvitesResult: Array<CollaboratorInvite> = [
  {
    idUser: 1,
    idUserAccount: 101,
    email: 'test@fromdoppler.com',
    firstname: 'Test',
    lastname: 'Test',
    invitationDate: '2024-08-15T02:12:09',
    expirationDate: '2024-08-16T13:19:37',
    sections: [1, 4, 8],
    invitationStatus: 'PENDING',
  },
  {
    idUser: 1,
    idUserAccount: 202,
    email: 'test2@fromdoppler.com',
    firstname: 'Test 2',
    lastname: 'Test 2',
    invitationDate: '2024-07-03T00:00:00',
    expirationDate: '2024-07-04T00:00:00',
    sections: [1, 2, 10, 18],
    invitationStatus: 'APPROVED',
  },
  ...Array.from({ length: 48 }, (_, index) => {
    const collaboratorNumber = index + 3;

    return {
      idUser: collaboratorNumber,
      idUserAccount: 200 + collaboratorNumber,
      email: `test${collaboratorNumber}@fromdoppler.com`,
      firstname: `Test ${collaboratorNumber}`,
      lastname: `Test ${collaboratorNumber}`,
      invitationDate: '2024-08-15T02:12:09',
      expirationDate: '2024-08-16T13:19:37',
      sections: [1, 4, 8],
      invitationStatus: index % 2 === 0 ? 'PENDING' : 'APPROVED',
    };
  }),
];

const collaboratorSectionsResult: Array<CollaboratorSection> = [
  { idSection: 1, name: 'Reports' },
  { idSection: 2, name: 'Campaigns' },
  { idSection: 3, name: 'Lists' },
  { idSection: 4, name: 'ControlPanel' },
  { idSection: 5, name: 'DownloadCenter' },
  { idSection: 8, name: 'Steps' },
  { idSection: 10, name: 'Automation' },
  { idSection: 11, name: 'Integration' },
  { idSection: 12, name: 'Templates' },
  { idSection: 13, name: 'Approver' },
  { idSection: 14, name: 'Dashboard' },
  { idSection: 15, name: 'Conversations' },
  { idSection: 16, name: 'PopUpHub' },
  { idSection: 18, name: 'Landings' },
];

const collaboratorSectionsWithoutApprover = collaboratorSectionsResult.filter(
  ({ idSection }) => idSection !== 13,
);

export class HardcodedDopplerUserApiClient implements DopplerUserApiClient {
  public async getContactInformationData(): Promise<
    ResultWithoutExpectedErrors<ContactInformation>
  > {
    console.log('getContactInformationData');
    await timeout(1500);

    return {
      value: fakeContactInformation,
      success: true,
    };
  }

  public async updateContactInformation(values: any): Promise<EmptyResultWithoutExpectedErrors> {
    await timeout(1500);
    console.log(values);
    return {
      success: true,
    };
  }

  async getFeatures(): Promise<ResultWithoutExpectedErrors<Features>> {
    console.log('getFeatures');
    await timeout(1500);

    return {
      value: featuresResult,
      success: true,
    };
  }

  async getIntegrationsStatus(): Promise<ResultWithoutExpectedErrors<IntegrationsStatus>> {
    console.log('getIntegrationsStatus');
    await timeout(1500);

    return {
      value: integrationsStatusResult,
      success: true,
    };
  }

  public async getAvailableCollaboratorSections(): Promise<
    ResultWithoutExpectedErrors<Array<CollaboratorSection>>
  > {
    await timeout(1500);
    return {
      success: true,
      value: collaboratorSectionsWithoutApprover,
    };
  }

  public async sendCollaboratorInvite(
    value: SendCollaboratorInviteData,
  ): Promise<EmptyResultWithoutExpectedErrors> {
    await timeout(1500);
    console.log(value);
    return {
      success: true,
    };
  }

  public async getCollaborationInvites(
    pageNumber = 0,
    pageSize = 10,
    search?: string,
  ): Promise<ResultWithoutExpectedErrors<BaseCollectionPage<CollaboratorInvite>>> {
    console.log('getCollaborationInvites');
    await timeout(1500);

    const normalizedSearch = search?.trim().toLowerCase();
    const filteredInvitations = normalizedSearch
      ? collaborationInvitesResult.filter(
          ({ email, firstname }) =>
            email.toLowerCase().includes(normalizedSearch) ||
            firstname.toLowerCase().includes(normalizedSearch),
        )
      : collaborationInvitesResult;

    return {
      success: true,
      value: {
        items: filteredInvitations.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize),
        currentPage: pageNumber,
        pageSize,
        itemsCount: filteredInvitations.length,
        pagesCount: Math.ceil(filteredInvitations.length / pageSize),
      },
    };
  }

  public async updateCollaborator(
    value: UpdateCollaboratorData,
  ): Promise<EmptyResultWithoutExpectedErrors> {
    await timeout(1500);
    console.log(value);
    return {
      success: true,
    };
  }

  public async cancelCollaboratorInvite(value: string): Promise<EmptyResultWithoutExpectedErrors> {
    await timeout(1500);
    console.log(value);
    return {
      success: true,
    };
  }

  public async updateUserAccountInformation(
    values: any,
  ): Promise<EmptyResultWithoutExpectedErrors> {
    console.log(values);
    await timeout(1500);

    return {
      success: true,
    };
  }

  public async getAccountNotificationSettings(): Promise<
    ResultWithoutExpectedErrors<AccountNotificationSettings>
  > {
    await timeout(1500);

    return {
      success: true,
      value: {
        weeklyAccountReportEnabled: true,
        conversationsMessageNotificationsEnabled: true,
        conversationsMessageNotificationSoundEnabled: false,
      },
    };
  }

  public async updateAccountNotificationSettings(
    values: AccountNotificationSettings,
  ): Promise<EmptyResultWithoutExpectedErrors> {
    console.log(values);
    await timeout(1500);

    return {
      success: true,
    };
  }
}
